import type {LinkField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import Head from 'next/head'
import {usePathname} from 'next/navigation'
import type {AllDocumentTypes} from 'prismicio-types'
import {Fragment} from 'react'
import {DEFAULT_LOCALE, GET_MOSS_HOSTNAME} from 'src/common/constants'

type AlternateLanguage = AllDocumentTypes['alternate_languages'][number] & {parentUid?: string}

type HrefLangElementsProps = {
  alternateLanguages: AlternateLanguage[]
  parentUid?: string | null
  pageUid?: string
  locale?: string
  canonicalUrl?: LinkField
}

export const HrefLangElements = ({
  alternateLanguages,
  parentUid,
  pageUid,
  locale,
  canonicalUrl,
}: HrefLangElementsProps) => {
  const path = usePathname()
  const currentPathIsDefaultLocale = isDefaultLocale(locale)

  return (
    <Head>
      {alternateLanguages.map((altLang, index) => (
        <Fragment key={altLang.id + index}>
          <link
            rel="alternate"
            href={generateHref(altLang.lang, altLang.parentUid, altLang.uid)}
            hrefLang={altLang.lang}
          />
          {isDefaultLocale(altLang.lang) && (
            <link
              rel="alternate"
              href={generateHref(altLang.lang, altLang.parentUid, altLang.uid)}
              hrefLang="x-default"
            />
          )}
        </Fragment>
      ))}
      <link rel="alternate" href={generateHref(locale, parentUid, pageUid)} hrefLang={locale} />
      {currentPathIsDefaultLocale && (
        <link
          key="x-default"
          rel="alternate"
          href={`https://www.getmoss.com${generateUrlPath(parentUid, pageUid)}`}
          hrefLang="x-default"
        />
      )}
      {isFilled.link(canonicalUrl) && canonicalUrl.url ? (
        <link rel="canonical" href={canonicalUrl.url} key="canonical" />
      ) : (
        <link
          rel="canonical"
          href={generateCanonicalUrl(GET_MOSS_HOSTNAME, currentPathIsDefaultLocale, locale, path)}
          key="canonical"
        />
      )}
    </Head>
  )
}

const generateHref = (lang?: string, parentUid?: string | null, uid?: string): string => {
  const baseUrl = 'https://www.getmoss.com'
  const langPath = lang === 'en-us' ? '' : `/${lang}`

  let uidPath = ''

  if (parentUid && uid) {
    uidPath = `/${parentUid}/${uid}`
  } else if (uid && uid !== 'home-page') {
    uidPath = `/${uid}`
  } else if (uid === 'home-page') {
    uidPath = ''
  }

  return `${baseUrl}${langPath}${uidPath}`
}

const generateUrlPath = (parentUid?: string | null, pageUid?: string): string => {
  if (parentUid) {
    return `/${parentUid}/${pageUid}`
  }

  if (pageUid === 'home-page') {
    return ''
  }

  return `/${pageUid}`
}

const generateCanonicalUrl = (hostname: string, isDefaultLocale: boolean, locale?: string, path?: string): string => {
  const localeSegment = isDefaultLocale ? '' : `/${locale}`
  const pathSegment = path || ''

  // Combine all parts and remove any trailing slash (except for root)
  const fullUrl = `${hostname}${localeSegment}${pathSegment}`

  return fullUrl === hostname ? fullUrl : fullUrl.replace(/\/$/, '')
}

const isDefaultLocale = (locale?: string) => locale === DEFAULT_LOCALE
